import {
  ICONE_DA_VISIBILIDADE,
  ROTULO_DA_VISIBILIDADE,
  ROTULO_DO_MODO,
  type SavedFilter,
  type Visao,
} from '@orbind/core'
import type { FilterComboOption } from '@orbind/ui'

/**
 * Regras puras das Visões na tela (SPEC-20261103-1418): ordem do combo, rótulo
 * de quem vê e a leitura do filtro do quadro.
 */

/** Minhas primeiro, depois as compartilhadas comigo, depois as públicas dos outros. */
export function ordenarVisoes(visoes: readonly Visao[]): Visao[] {
  const grupo = (v: Visao) => (v.mine ? 0 : v.visibility === 'shared' ? 1 : 2)
  return [...visoes].sort((a, b) => grupo(a) - grupo(b) || a.sortOrder - b.sortOrder)
}

/** "Privada · Lista", "Pública · de Ana · Quadro" — o texto miúdo do combo. */
export function dicaDaVisao(v: Visao): string {
  const partes: string[] = [ROTULO_DA_VISIBILIDADE[v.visibility]]
  if (!v.mine) partes.push(`de ${v.owner.name.split(' ')[0]}`)
  if (v.config.modo) partes.push(ROTULO_DO_MODO[v.config.modo])
  return partes.join(' · ')
}

/** As opções do combo: lápis para quem edita, lixeira só para o dono. */
export function opcoesDoCombo(visoes: readonly Visao[]): FilterComboOption[] {
  return ordenarVisoes(visoes).map((v) => ({
    id: v.id,
    name: v.name,
    icone: ICONE_DA_VISIBILIDADE[v.visibility],
    dica: dicaDaVisao(v),
    editavel: v.canEdit,
    removivel: v.mine,
  }))
}

/**
 * O filtro de uma visão de QUADRO como `SavedFilter`, que é o que o quadro
 * avalia. Sem filtro (ou ilegível) = não recorta nada.
 */
export function filtroDoQuadro(v: Visao | null | undefined): SavedFilter | null {
  const f = v?.config.filtro as { join?: unknown; criteria?: unknown } | null | undefined
  if (!v || !f || !Array.isArray(f.criteria)) return null
  return {
    id: v.id,
    name: v.name,
    join: f.join === 'OR' ? 'OR' : 'AND',
    criteria: f.criteria as SavedFilter['criteria'],
    ...(v.config.colunas ? { columns: v.config.colunas } : {}),
  }
}

/** O que vai para `config.filtro` a partir do construtor do quadro. */
export function filtroParaGuardar(f: Pick<SavedFilter, 'join' | 'criteria'>): Record<string, unknown> {
  return { join: f.join, criteria: f.criteria }
}
